import React from 'react';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { usePagination } from 'further-ui/hooks';
import FormWrapper, {
  AddCommitmentsToEqualisationFormValues,
} from './FormWrapper';
import AddCommitmentsToEqualisationForm from './AddCommitmentsToEqualisationForm';
import {
  calculateCPCapitalFromPercentage,
  getCommitmentsWithProRataCapitalContribution,
  getCommitmentsWithFixedCapitalContribution,
  getCommitmentsForManualCapitalContribution,
} from './utils';
import { sumBy } from 'lodash';
import { CapitalContributionRule } from 'further-types/lp/vehicle';

const pageId = 'commitments-for-equalisations';

interface AddCommitmentsToEqualisationProps {
  savedEqualisation?: {
    commitmentIds: Array<string>;
    name: string;
    closeDate: Date;
    id: string;
  };
}

const AddCommitmentsToEqualisation: React.FC<
  AddCommitmentsToEqualisationProps
> = ({ savedEqualisation }: AddCommitmentsToEqualisationProps) => {
  const { name: savedEqualisationName, closeDate: savedCloseDate } =
    savedEqualisation ?? {};

  const pagination = usePagination({
    orderBy: 'commitmentDate',
    order: 'desc',
    id: pageId,
  });

  const { vehicle } = useLayoutContext();
  const [commitmentsData] = api.commitments.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
    pendingClose: true,
    ...pagination.properties,
  });

  const commitmentsWithSelected =
    commitmentsData?.results?.map((c) => ({
      ...c,
      isSelected: true,
    })) ?? [];

  const isProRata =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.ProRataOfFixedPartnershipCapital;
  const isFixedCapitalContribution =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.FixedCommitmentPercentage;

  const commitments = isProRata
    ? getCommitmentsWithProRataCapitalContribution({
        commitments: commitmentsWithSelected.map((commitment) => ({
          ...commitment,
          capitalCommitmentLcy: commitment.isCarryPartnerCommitment
            ? commitment.capitalCommitmentLcy
            : 0,
        })),
        vehicleFixedCapitalAmount: vehicle?.fixedCapitalAmount ?? 0,
        capitalContributionPercentage:
          vehicle?.carrySettings?.capitalContributionPercentage,
      })
    : isFixedCapitalContribution
    ? getCommitmentsWithFixedCapitalContribution({
        commitments: commitmentsWithSelected,
        capitalContributionPercentage: vehicle?.capitalContributionPercentage,
      })
    : getCommitmentsForManualCapitalContribution({
        commitments: commitmentsWithSelected,
      });

  const nonCarryPartnerCapitalContributionTotalLcy = sumBy(
    commitments.filter((c) => !c.isCarryPartnerCommitment),
    'capitalCommitmentLcy',
  );

  const mappedCommitments = commitments.map((commitment) => {
    const isCpUsesCCPercentageAndIsNotProRata =
      commitment.isCarryPartnerCommitment &&
      vehicle?.carrySettings?.capitalContributionPercentage &&
      vehicle?.capitalContributionRule !==
        CapitalContributionRule.ProRataOfFixedPartnershipCapital;

    const capitalCommitmentLcy = isCpUsesCCPercentageAndIsNotProRata
      ? calculateCPCapitalFromPercentage({
          capitalContributionPercentage:
            vehicle?.carrySettings?.capitalContributionPercentage ?? 0,
          nonCarryPartnerCapitalContributionTotalLcy,
        })
      : commitment.capitalCommitmentLcy;

    const totalCommitmentLcy = isCpUsesCCPercentageAndIsNotProRata
      ? capitalCommitmentLcy + (commitment.loanCommitmentLcy ?? 0)
      : commitment.totalCommitmentLcy;

    return {
      id: commitment.id,
      investorName: commitment.investorName,
      totalCommitmentLcy,
      loanCommitmentLcy: commitment.loanCommitmentLcy,
      capitalCommitmentLcy,
      isCarryPartnerCommitment: commitment.isCarryPartnerCommitment ?? false,
      isCapitalContributionRuleOverride:
        commitment.isCapitalContributionRuleOverride ?? false,
      isSelected: commitment.isSelected,
    };
  });

  const formValues: AddCommitmentsToEqualisationFormValues = {
    closeDate: savedCloseDate ?? new Date(),
    name: savedEqualisationName ?? '',
    commitments: mappedCommitments,
  };

  return (
    <FormWrapper values={formValues}>
      <AddCommitmentsToEqualisationForm savedEqualisation={savedEqualisation} />
    </FormWrapper>
  );
};

export default AddCommitmentsToEqualisation;
